import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectDealerSelected } from 'app/pages/Header/selectors';
import GuidanceTourPanel from './components/GuidanceTourPanel';
import GuidanceTourSpotlight from './components/GuidanceTourSpotlight';
import OnboardingLaunchPromptModal from './components/OnboardingLaunchPromptModal';
import {
  GuidanceTourConfig,
  GuidanceTourContextValue,
  GuidanceTourStepCompletionMap,
  GuidanceTourStorageState,
  OnboardingStepContextMap,
  OnboardingStepAccessibilityMap,
  OnboardingStepValidationResult,
  StartTourParams,
  TourProgressSummary,
} from './types';
import {
  areAllStepsCompleted,
  canMarkStepComplete,
  countIncompletePriorSteps,
  findFirstIncompleteIndex,
  findNextIncompleteIndex,
  findPreviousIncompleteIndex,
  findResumeStepIndex,
  getTourProgress,
  getTourStorage,
  hasSavedTourProgress,
  initStepCompleted,
  isStepCompleted,
  isTourResumable,
} from './utils/tourStorage';
import {
  fetchStepValidation,
  getTourStorageFromApi,
  isApiDrivenTour,
  markStepCompletedInApi,
  markTourStatusInApi,
  resetTourProgressInApi,
  saveTourStorageToApi,
} from './utils/tourApiStorage';
import { markStepCompleted as markStepCompletedLocal } from './utils/tourStorage';
import {
  expandAccordionTarget,
  getTargetRect,
  matchesTourRoute,
  scrollTargetIntoView,
  waitForElement,
  waitForTourRoute,
} from './utils/waitForElement';
import {
  clearOnboardingLaunchPromptSeen,
  hasSeenOnboardingLaunchPrompt,
  markOnboardingLaunchPromptSeen,
} from './utils/onboardingLaunchPromptStorage';
import DealerOnboardingApiService from 'app/apiService/dealerOnboarding/dealerOnboardingApiService';
import DealerContentSettingsApiService from 'app/apiService/dealerContentSettings/dealerContentSettingsApiService';
import {
  buildCrmSupportsEmailReportsStepContext,
  buildSandboxReadinessRequiredStepContext,
  CDP_SETTINGS_SETUP_STEP_ID,
  getCrmSupportsEmailReportsSelection,
  getSandboxReadinessRequiredSelection,
  MYAUTOIQ_SETTINGS_SETUP_STEP_ID,
  resolveCdpSettingsCanComplete,
  resolveSandboxReadinessCanComplete,
  SANDBOX_READINESS_SETUP_STEP_ID,
} from './utils/onboardingStepContext';

const GuidanceTourContext = createContext<GuidanceTourContextValue | null>(
  null,
);

const getDealerId = (dealer: any): string | null => {
  if (!dealer || dealer === 'default') return null;
  return dealer.dealer || dealer._id || null;
};

export const GuidanceTourProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const history = useHistory();
  const selectedDealer = useSelector(selectDealerSelected);

  const [config, setConfig] = useState<GuidanceTourConfig | null>(null);
  const [dealerId, setDealerId] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [stepCompleted, setStepCompleted] = useState<GuidanceTourStepCompletionMap>(
    {},
  );
  const [isActive, setIsActive] = useState(false);
  const [isChecklistOpen, setIsChecklistOpen] = useState(true);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [isPreparingStep, setIsPreparingStep] = useState(false);
  const [isStartingTour, setIsStartingTour] = useState(false);
  const [tourStorageRevision, setTourStorageRevision] = useState(0);
  const [stepValidation, setStepValidation] =
    useState<OnboardingStepValidationResult | null>(null);
  const [isLoadingStepValidation, setIsLoadingStepValidation] = useState(false);
  const [markCompleteError, setMarkCompleteError] = useState<string | null>(null);
  const [isMarkingComplete, setIsMarkingComplete] = useState(false);
  const [launchPromptOpen, setLaunchPromptOpen] = useState(false);
  const [launchPromptDealerId, setLaunchPromptDealerId] = useState<string | null>(
    null,
  );
  const [stepContext, setStepContext] = useState<OnboardingStepContextMap>({});
  const [stepAccessibility, setStepAccessibility] =
    useState<OnboardingStepAccessibilityMap>({});
  const [isSavingStepContext, setIsSavingStepContext] = useState(false);
  const [launchIntroActive, setLaunchIntroActive] = useState(false);

  const bumpStorageRevision = useCallback(() => {
    setTourStorageRevision(revision => revision + 1);
  }, []);

  const openLaunchPromptIfNeeded = useCallback(
    (tourId: string, activeDealerId: string) => {
      if (
        !isApiDrivenTour(tourId) ||
        !activeDealerId ||
        hasSeenOnboardingLaunchPrompt(activeDealerId)
      ) {
        return;
      }

      setLaunchPromptDealerId(activeDealerId);
      setLaunchPromptOpen(true);
    },
    [],
  );

  const dismissLaunchPrompt = useCallback(() => {
    if (launchPromptDealerId) {
      markOnboardingLaunchPromptSeen(launchPromptDealerId);
    }
    setLaunchPromptOpen(false);
    setLaunchPromptDealerId(null);
  }, [launchPromptDealerId]);

  const goToNurturingPlanGeneratorFromPrompt = useCallback(() => {
    if (launchPromptDealerId) {
      markOnboardingLaunchPromptSeen(launchPromptDealerId);
    }
    setLaunchPromptOpen(false);
    setLaunchPromptDealerId(null);
    history.push('/nurturing-plan-generator', {
      breadcrumbsList: [
        { label: 'Configure', link: '/configure', isActive: false },
        { label: 'Nurturing Plan Generator', isActive: true },
      ],
    });
  }, [history, launchPromptDealerId]);

  const prepareStepRef = useRef(0);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const targetRectRafRef = useRef<number | null>(null);
  const observedElementRef = useRef<HTMLElement | null>(null);
  const stepCompletedRef = useRef(stepCompleted);
  stepCompletedRef.current = stepCompleted;
  const stepAccessibilityRef = useRef(stepAccessibility);
  stepAccessibilityRef.current = stepAccessibility;

  const currentStep = config?.steps[currentStepIndex] ?? null;

  const crmSupportsEmailReportsSelection = useMemo(
    () => getCrmSupportsEmailReportsSelection(stepContext),
    [stepContext],
  );

  const crmSupportsEmailReports = crmSupportsEmailReportsSelection === true;

  const sandboxReadinessRequiredSelection = useMemo(
    () => getSandboxReadinessRequiredSelection(stepContext),
    [stepContext],
  );

  const sandboxReadinessRequired = sandboxReadinessRequiredSelection === true;

  const isCurrentStepCompleted = currentStep
    ? isStepCompleted(stepCompleted, currentStep.id)
    : false;

  const priorIncompleteCount = useMemo(() => {
    if (!config) return 0;
    return countIncompletePriorSteps(config, stepCompleted, currentStepIndex);
  }, [config, currentStepIndex, stepCompleted]);

  const canMarkCurrentStepComplete = useMemo(() => {
    if (!config || isCurrentStepCompleted) return false;
    if (!canMarkStepComplete(config, stepCompleted, currentStepIndex)) {
      return false;
    }
    if (isApiDrivenTour(config.id)) {
      if (isLoadingStepValidation) return false;
      if (stepValidation && stepValidation.step_id === currentStep?.id) {
        if (currentStep.id === CDP_SETTINGS_SETUP_STEP_ID) {
          if (crmSupportsEmailReportsSelection === null) {
            return false;
          }
          return resolveCdpSettingsCanComplete(
            stepValidation,
            crmSupportsEmailReportsSelection,
          );
        }
        if (currentStep.id === SANDBOX_READINESS_SETUP_STEP_ID) {
          if (sandboxReadinessRequiredSelection === null) {
            return false;
          }
          return resolveSandboxReadinessCanComplete(
            stepValidation,
            sandboxReadinessRequiredSelection,
          );
        }
        return stepValidation.can_complete;
      }
    }
    return true;
  }, [
    config,
    crmSupportsEmailReportsSelection,
    currentStep?.id,
    currentStepIndex,
    isCurrentStepCompleted,
    isLoadingStepValidation,
    sandboxReadinessRequiredSelection,
    stepCompleted,
    stepValidation,
  ]);

  const canNavigateBack = useMemo(() => {
    if (!config) return false;
    return findPreviousIncompleteIndex(config, stepCompleted, currentStepIndex) !== null;
  }, [config, currentStepIndex, stepCompleted]);

  const canNavigateNext = useMemo(() => {
    if (!config) return false;
    return findNextIncompleteIndex(config, stepCompleted, currentStepIndex) !== null;
  }, [config, currentStepIndex, stepCompleted]);

  const progressSummary = useMemo((): TourProgressSummary | null => {
    if (!config) return null;
    const storage: GuidanceTourStorageState = {
      status: 'active',
      currentStepIndex,
      dealerId: dealerId ?? '',
      updatedAt: Date.now(),
      stepCompleted,
    };
    return getTourProgress(config, storage);
  }, [config, currentStepIndex, dealerId, stepCompleted]);

  const progressPercent = progressSummary?.percent ?? 0;

  const getStoredProgress = useCallback(
    (tourConfig: GuidanceTourConfig, tourDealerId: string) => {
      const stored = getTourStorage(tourConfig.id, tourDealerId, tourConfig);
      return getTourProgress(tourConfig, stored);
    },
    [],
  );

  const clearTargetObserver = useCallback(() => {
    if (targetRectRafRef.current !== null) {
      window.cancelAnimationFrame(targetRectRafRef.current);
      targetRectRafRef.current = null;
    }
    resizeObserverRef.current?.disconnect();
    resizeObserverRef.current = null;
    observedElementRef.current = null;
  }, []);

  const updateTargetRect = useCallback(
    (element: HTMLElement | null) => {
      if (!element) {
        setTargetRect(null);
        clearTargetObserver();
        return;
      }

      setTargetRect(getTargetRect(element));
      observedElementRef.current = element;
      clearTargetObserver();

      resizeObserverRef.current = new ResizeObserver(() => {
        if (targetRectRafRef.current !== null) {
          return;
        }

        targetRectRafRef.current = window.requestAnimationFrame(() => {
          targetRectRafRef.current = null;
          const element = observedElementRef.current;
          if (!element) {
            return;
          }

          const nextRect = getTargetRect(element);
          setTargetRect(prev => {
            if (
              prev &&
              prev.top === nextRect.top &&
              prev.left === nextRect.left &&
              prev.width === nextRect.width &&
              prev.height === nextRect.height
            ) {
              return prev;
            }

            return nextRect;
          });
        });
      });
      resizeObserverRef.current.observe(element);
    },
    [clearTargetObserver],
  );

  const refreshStepValidation = useCallback(async () => {
    if (!config || !dealerId || !currentStep) {
      setStepValidation(null);
      return;
    }

    if (!isApiDrivenTour(config.id)) {
      setStepValidation(null);
      return;
    }

    setIsLoadingStepValidation(true);
    try {
      const validation = await fetchStepValidation(dealerId, currentStep.id, {
        crmSupportsEmailReports:
          currentStep.id === CDP_SETTINGS_SETUP_STEP_ID &&
          crmSupportsEmailReportsSelection !== null
            ? crmSupportsEmailReportsSelection
            : undefined,
        sandboxReadinessRequired:
          currentStep.id === SANDBOX_READINESS_SETUP_STEP_ID &&
          sandboxReadinessRequiredSelection !== null
            ? sandboxReadinessRequiredSelection
            : undefined,
      });
      setStepValidation(validation);
      setMarkCompleteError(null);
    } catch {
      setStepValidation(null);
    } finally {
      setIsLoadingStepValidation(false);
    }
  }, [
    config,
    crmSupportsEmailReportsSelection,
    currentStep,
    dealerId,
    sandboxReadinessRequiredSelection,
  ]);

  const refreshTourProgress = useCallback(async () => {
    // Always bump so Launch Readiness launcher re-reads progress from API.
    bumpStorageRevision();

    if (!config || !dealerId || !isApiDrivenTour(config.id)) {
      return;
    }

    try {
      const stored = await getTourStorageFromApi(config.id, dealerId, config);
      if (stored?.stepCompleted) {
        setStepCompleted(stored.stepCompleted);
      }
      if (stored?.stepContext) {
        setStepContext(stored.stepContext);
      }
      if (stored?.stepAccessibility) {
        setStepAccessibility(stored.stepAccessibility);
      }
    } catch {
      // Best-effort refresh; launcher bump still applies.
    }

    if (isActive) {
      await refreshStepValidation();
    }
  }, [
    bumpStorageRevision,
    config,
    dealerId,
    isActive,
    refreshStepValidation,
  ]);

  const updateCrmSupportsEmailReports = useCallback(
    async (enabled: boolean) => {
      if (!dealerId) return;

      setIsSavingStepContext(true);
      try {
        const nextStepContext = buildCrmSupportsEmailReportsStepContext(enabled);
        setStepContext(prev => ({
          ...prev,
          ...nextStepContext,
        }));
        await DealerOnboardingApiService.saveProgress(dealerId, {
          step_context: nextStepContext,
        });
        bumpStorageRevision();

        if (
          config &&
          isApiDrivenTour(config.id) &&
          currentStep?.id === CDP_SETTINGS_SETUP_STEP_ID
        ) {
          setIsLoadingStepValidation(true);
          try {
            const validation = await fetchStepValidation(dealerId, currentStep.id, {
              crmSupportsEmailReports: enabled,
            });
            setStepValidation(validation);
            setMarkCompleteError(null);
          } catch {
            setStepValidation(null);
          } finally {
            setIsLoadingStepValidation(false);
          }
        }
      } finally {
        setIsSavingStepContext(false);
      }
    },
    [bumpStorageRevision, config, currentStep, dealerId],
  );

  const updateSandboxReadinessRequired = useCallback(
    async (required: boolean) => {
      if (!dealerId) return;

      setIsSavingStepContext(true);
      try {
        const nextStepContext = buildSandboxReadinessRequiredStepContext(required);
        setStepContext(prev => ({
          ...prev,
          ...nextStepContext,
        }));
        await DealerOnboardingApiService.saveProgress(dealerId, {
          step_context: nextStepContext,
        });
        bumpStorageRevision();

        if (
          config &&
          isApiDrivenTour(config.id) &&
          currentStep?.id === SANDBOX_READINESS_SETUP_STEP_ID
        ) {
          setIsLoadingStepValidation(true);
          try {
            const validation = await fetchStepValidation(dealerId, currentStep.id, {
              sandboxReadinessRequired: required,
            });
            setStepValidation(validation);
            setMarkCompleteError(null);
          } catch {
            setStepValidation(null);
          } finally {
            setIsLoadingStepValidation(false);
          }
        }
      } finally {
        setIsSavingStepContext(false);
      }
    },
    [bumpStorageRevision, config, currentStep, dealerId],
  );

  const persistActiveStep = useCallback(
    (
      activeConfig: GuidanceTourConfig,
      activeDealerId: string,
      stepIndex: number,
      completedMap: GuidanceTourStepCompletionMap,
    ) => {
      void saveTourStorageToApi(
        activeConfig.id,
        activeDealerId,
        {
          status: 'active',
          currentStepIndex: stepIndex,
          lastViewedStepId: activeConfig.steps[stepIndex]?.id,
          hasPausedSession: true,
        },
        activeConfig,
      ).then(() => {
        bumpStorageRevision();
      });
    },
    [bumpStorageRevision],
  );

  const prepareStep = useCallback(
    async (
      stepIndex: number,
      activeConfig: GuidanceTourConfig,
      activeDealerId: string,
      completedMap: GuidanceTourStepCompletionMap,
    ) => {
      const step = activeConfig.steps[stepIndex];
      if (!step) return;

      const requestId = ++prepareStepRef.current;
      setIsPreparingStep(true);
      setTargetRect(null);

      let tourRoute = step.route;
      if (step.id === MYAUTOIQ_SETTINGS_SETUP_STEP_ID && tourRoute) {
        try {
          const response = await DealerContentSettingsApiService.getDealerContentSettings(
            activeDealerId,
          );
          const manufacturer = (response?.body as { manufacturer?: string } | undefined)
            ?.manufacturer;
          if (manufacturer) {
            tourRoute = `/myautoiq-settings?manufacturer_id=${encodeURIComponent(manufacturer)}`;
          }
        } catch {
          tourRoute = step.route;
        }
      }

      if (tourRoute && !matchesTourRoute(tourRoute)) {
        history.push(tourRoute);
        await waitForTourRoute(tourRoute);
        await new Promise(resolve => window.setTimeout(resolve, 150));
      }

      if (step.target) {
        const element = await waitForElement(step.target);
        if (requestId !== prepareStepRef.current) return;

        if (element) {
          if (step.expandAccordion) {
            expandAccordionTarget(element);
            await new Promise(resolve => window.setTimeout(resolve, 250));
          }

          if (step.scrollIntoView !== false) {
            scrollTargetIntoView(element);
          }

          updateTargetRect(element);
        } else {
          updateTargetRect(null);
        }
      } else {
        updateTargetRect(null);
      }

      if (requestId !== prepareStepRef.current) return;

      persistActiveStep(activeConfig, activeDealerId, stepIndex, completedMap);
      setIsPreparingStep(false);

      if (isApiDrivenTour(activeConfig.id)) {
        setIsLoadingStepValidation(true);
        try {
          let validationCrmSelection: boolean | null = null;
          let validationSandboxSelection: boolean | null = null;
          if (
            step.id === CDP_SETTINGS_SETUP_STEP_ID ||
            step.id === SANDBOX_READINESS_SETUP_STEP_ID
          ) {
            const progress = await DealerOnboardingApiService.getProgress(activeDealerId);
            const loadedContext = progress.step_context ?? {};
            setStepContext(loadedContext);
            validationCrmSelection = getCrmSupportsEmailReportsSelection(loadedContext);
            validationSandboxSelection = getSandboxReadinessRequiredSelection(loadedContext);
          }

          const validation = await fetchStepValidation(activeDealerId, step.id, {
            crmSupportsEmailReports:
              step.id === CDP_SETTINGS_SETUP_STEP_ID &&
              validationCrmSelection !== null
                ? validationCrmSelection
                : undefined,
            sandboxReadinessRequired:
              step.id === SANDBOX_READINESS_SETUP_STEP_ID &&
              validationSandboxSelection !== null
                ? validationSandboxSelection
                : undefined,
          });
          if (requestId !== prepareStepRef.current) return;
          setStepValidation(validation);
          setMarkCompleteError(null);
        } catch {
          if (requestId !== prepareStepRef.current) return;
          setStepValidation(null);
        } finally {
          if (requestId === prepareStepRef.current) {
            setIsLoadingStepValidation(false);
          }
        }
      } else {
        setStepValidation(null);
      }
    },
    [history, persistActiveStep, updateTargetRect],
  );

  const hideTourUi = useCallback(() => {
    prepareStepRef.current += 1;
    clearTargetObserver();
    setLaunchIntroActive(false);
    setIsActive(false);
    setIsChecklistOpen(false);
    setConfig(null);
    setDealerId(null);
    setCurrentStepIndex(0);
    setStepCompleted({});
    setStepAccessibility({});
    setTargetRect(null);
    setIsPreparingStep(false);
    setStepValidation(null);
    setMarkCompleteError(null);
  }, [clearTargetObserver]);

  const stopTour = useCallback(
    (status: 'completed' | 'ended', stepIndex: number) => {
      if (config && dealerId) {
        void markTourStatusInApi(
          config.id,
          dealerId,
          config,
          status,
          stepIndex,
          stepCompletedRef.current,
        ).then(() => {
          bumpStorageRevision();
        });
      } else {
        bumpStorageRevision();
      }
      hideTourUi();
    },
    [bumpStorageRevision, config, dealerId, hideTourUi],
  );

  const goToStep = useCallback(
    async (
      stepIndex: number,
      activeConfig: GuidanceTourConfig,
      activeDealerId: string,
      options?: { bypassAccessibility?: boolean },
    ) => {
      const targetStep = activeConfig.steps[stepIndex];
      if (!targetStep) {
        return;
      }

      if (!options?.bypassAccessibility) {
        const accessibility = stepAccessibilityRef.current[targetStep.id];
        if (accessibility && !accessibility.accessible) {
          return;
        }
      }

      const safeIndex = Math.min(
        Math.max(stepIndex, 0),
        activeConfig.steps.length - 1,
      );
      setCurrentStepIndex(safeIndex);
      void prepareStep(
        safeIndex,
        activeConfig,
        activeDealerId,
        stepCompletedRef.current,
      );
    },
    [prepareStep],
  );

  const finishTourIfComplete = useCallback(
    async (
      activeConfig: GuidanceTourConfig,
      activeDealerId: string,
      stepIndex: number,
    ) => {
      if (!areAllStepsCompleted(activeConfig, stepCompletedRef.current)) {
        return false;
      }

      await markTourStatusInApi(
        activeConfig.id,
        activeDealerId,
        activeConfig,
        'completed',
        stepIndex,
        stepCompletedRef.current,
      );
      bumpStorageRevision();
      hideTourUi();
      openLaunchPromptIfNeeded(activeConfig.id, activeDealerId);
      return true;
    },
    [bumpStorageRevision, hideTourUi, openLaunchPromptIfNeeded],
  );

  const startTour = useCallback(
  async ({
      config: tourConfig,
      dealerId: tourDealerId,
      startAtStep = 0,
      resetProgress = false,
      resume = false,
    }: StartTourParams) => {
      setIsStartingTour(true);
      try {
      prepareStepRef.current += 1;
      clearTargetObserver();

      let completedMap = initStepCompleted(tourConfig);
      let initialIndex = startAtStep;
      let initialStepContext: OnboardingStepContextMap = {};
      let initialStepAccessibility: OnboardingStepAccessibilityMap = {};
      let introAcknowledged = false;

      if (resetProgress) {
        clearOnboardingLaunchPromptSeen(tourDealerId);
        const resetState = await resetTourProgressInApi(
          tourConfig.id,
          tourDealerId,
          tourConfig,
        );
        completedMap = resetState.stepCompleted;
        initialStepContext = resetState.stepContext ?? {};
        initialStepAccessibility = resetState.stepAccessibility ?? {};
        introAcknowledged = resetState.hasLaunchReadinessIntroAcknowledged === true;
        bumpStorageRevision();
      } else {
        const stored = await getTourStorageFromApi(
          tourConfig.id,
          tourDealerId,
          tourConfig,
        );
        if (stored) {
          completedMap = stored.stepCompleted;
          initialStepContext = stored.stepContext ?? {};
          initialStepAccessibility = stored.stepAccessibility ?? {};
          introAcknowledged = stored.hasLaunchReadinessIntroAcknowledged === true;
          if (resume) {
            initialIndex = findResumeStepIndex(tourConfig, stored);
          } else if (startAtStep === 0 && stored.currentStepIndex > 0) {
            initialIndex = stored.currentStepIndex;
          } else {
            initialIndex = startAtStep;
          }
        }
      }

      if (!resume) {
        const currentStepId = tourConfig.steps[initialIndex]?.id;
        if (currentStepId && isStepCompleted(completedMap, currentStepId)) {
          initialIndex = findFirstIncompleteIndex(tourConfig, completedMap);
        }
      }

      const safeStepIndex = Math.min(
        Math.max(initialIndex, 0),
        tourConfig.steps.length - 1,
      );

      setConfig(tourConfig);
      setDealerId(tourDealerId);
      setStepCompleted(completedMap);
      setStepContext(initialStepContext);
      setStepAccessibility(initialStepAccessibility);
      setCurrentStepIndex(safeStepIndex);
      setIsActive(true);
      setIsChecklistOpen(false);

      const shouldShowLaunchIntro =
        !introAcknowledged && !Object.values(completedMap).some(Boolean);
      setLaunchIntroActive(shouldShowLaunchIntro);

      persistActiveStep(tourConfig, tourDealerId, safeStepIndex, completedMap);
      if (!shouldShowLaunchIntro) {
        void prepareStep(safeStepIndex, tourConfig, tourDealerId, completedMap);
      }
      } finally {
        setIsStartingTour(false);
      }
    },
    [bumpStorageRevision, clearTargetObserver, persistActiveStep, prepareStep],
  );

  const dismissLaunchIntro = useCallback(() => {
    if (!config || !dealerId) return;
    setLaunchIntroActive(false);
    void saveTourStorageToApi(
      config.id,
      dealerId,
      {
        status: 'active',
        currentStepIndex,
        hasLaunchReadinessIntroAcknowledged: true,
      },
      config,
    ).then(() => {
      bumpStorageRevision();
    });
    void prepareStep(
      currentStepIndex,
      config,
      dealerId,
      stepCompletedRef.current,
    );
  }, [bumpStorageRevision, config, currentStepIndex, dealerId, prepareStep]);

  const continueTour = useCallback(
    async (tourConfig: GuidanceTourConfig, tourDealerId: string) => {
      await startTour({
        config: tourConfig,
        dealerId: tourDealerId,
        resume: true,
      });
    },
    [startTour],
  );

  const restartTour = useCallback(() => {
    if (!config || !dealerId) return;
    void startTour({
      config,
      dealerId,
      startAtStep: 0,
      resetProgress: true,
    });
  }, [config, dealerId, startTour]);

  const markComplete = useCallback(async () => {
    if (!config || !dealerId || !currentStep) return;

    if (
      !canMarkStepComplete(
        config,
        stepCompletedRef.current,
        currentStepIndex,
      )
    ) {
      return;
    }

    const canCompleteCurrentStep =
      stepValidation && stepValidation.step_id === currentStep.id
        ? currentStep.id === CDP_SETTINGS_SETUP_STEP_ID
          ? crmSupportsEmailReportsSelection !== null &&
            resolveCdpSettingsCanComplete(
              stepValidation,
              crmSupportsEmailReportsSelection,
            )
          : currentStep.id === SANDBOX_READINESS_SETUP_STEP_ID
            ? sandboxReadinessRequiredSelection !== null &&
              resolveSandboxReadinessCanComplete(
                stepValidation,
                sandboxReadinessRequiredSelection,
              )
            : stepValidation.can_complete
        : true;

    if (
      isApiDrivenTour(config.id) &&
      currentStep.id === CDP_SETTINGS_SETUP_STEP_ID &&
      crmSupportsEmailReportsSelection === null
    ) {
      setMarkCompleteError('Select Yes or No for CRM email reports before continuing.');
      return;
    }

    if (
      isApiDrivenTour(config.id) &&
      currentStep.id === SANDBOX_READINESS_SETUP_STEP_ID &&
      sandboxReadinessRequiredSelection === null
    ) {
      setMarkCompleteError('Select Yes or No for sandbox readiness before continuing.');
      return;
    }

    if (isApiDrivenTour(config.id) && stepValidation && !canCompleteCurrentStep) {
      setMarkCompleteError(
        'Complete all required checklist items before marking this step complete.',
      );
      return;
    }

    setIsMarkingComplete(true);
    try {
      if (!isStepCompleted(stepCompletedRef.current, currentStep.id)) {
        try {
          if (isApiDrivenTour(config.id)) {
            const progress = await markStepCompletedInApi(
              config.id,
              dealerId,
              config,
              currentStep.id,
              currentStepIndex,
            );
            const updatedCompleted = progress.stepCompleted;
            setStepCompleted(updatedCompleted);
            stepCompletedRef.current = updatedCompleted;
            setStepAccessibility(progress.stepAccessibility ?? {});
            stepAccessibilityRef.current = progress.stepAccessibility ?? {};
          } else {
            const updatedCompleted = {
              ...stepCompletedRef.current,
              [currentStep.id]: true,
            };
            setStepCompleted(updatedCompleted);
            stepCompletedRef.current = updatedCompleted;
            markStepCompletedLocal(
              config.id,
              dealerId,
              config,
              currentStep.id,
              true,
              currentStepIndex,
            );
          }
          bumpStorageRevision();
          setMarkCompleteError(null);
        } catch (error: any) {
          const validation =
            error?.responseData?.body?.checklist != null
              ? error.responseData.body
              : null;
          if (validation) {
            setStepValidation(validation);
          } else {
            await refreshStepValidation();
          }
          setMarkCompleteError(
            error?.message ||
              'Complete all required checklist items before marking this step complete.',
          );
          return;
        }
      }

      if (await finishTourIfComplete(config, dealerId, currentStepIndex)) {
        return;
      }

      const nextIndex = findNextIncompleteIndex(
        config,
        stepCompletedRef.current,
        currentStepIndex,
      );

      if (nextIndex === null) {
        return;
      }

      goToStep(nextIndex, config, dealerId);
    } finally {
      setIsMarkingComplete(false);
    }
  }, [
    bumpStorageRevision,
    config,
    crmSupportsEmailReportsSelection,
    currentStep,
    currentStepIndex,
    dealerId,
    finishTourIfComplete,
    goToStep,
    refreshStepValidation,
    sandboxReadinessRequiredSelection,
    stepValidation,
  ]);

  const nextStep = useCallback(() => {
    if (!config || !dealerId) return;

    const nextIndex = findNextIncompleteIndex(
      config,
      stepCompletedRef.current,
      currentStepIndex,
    );

    if (nextIndex === null) {
      return;
    }

    goToStep(nextIndex, config, dealerId);
  }, [config, currentStepIndex, dealerId, goToStep]);

  const goBack = useCallback(() => {
    if (!config || !dealerId) return;

    const previousIndex = findPreviousIncompleteIndex(
      config,
      stepCompletedRef.current,
      currentStepIndex,
    );

    if (previousIndex === null) {
      return;
    }

    goToStep(previousIndex, config, dealerId);
  }, [config, currentStepIndex, dealerId, goToStep]);

  const saveAndExit = useCallback(() => {
    if (!config || !dealerId) return;
    void saveTourStorageToApi(
      config.id,
      dealerId,
      {
        status: 'active',
        currentStepIndex,
        lastViewedStepId: config.steps[currentStepIndex]?.id,
        hasPausedSession: true,
      },
      config,
    ).then(() => {
      bumpStorageRevision();
    });
    hideTourUi();
  }, [bumpStorageRevision, config, currentStepIndex, dealerId, hideTourUi]);

  const endTourPermanently = useCallback(() => {
    stopTour('ended', currentStepIndex);
  }, [currentStepIndex, stopTour]);

  const jumpToStep = useCallback(
    (index: number) => {
      if (!config || !dealerId) return;
      goToStep(index, config, dealerId);
    },
    [config, dealerId, goToStep],
  );

  const jumpToPrerequisiteStep = useCallback(
    (stepId: string) => {
      if (!config || !dealerId) return;
      const index = config.steps.findIndex((step) => step.id === stepId);
      if (index < 0) {
        return;
      }
      goToStep(index, config, dealerId, { bypassAccessibility: true });
    },
    [config, dealerId, goToStep],
  );

  const openChecklist = useCallback(() => setIsChecklistOpen(true), []);
  const closeChecklist = useCallback(() => setIsChecklistOpen(false), []);

  useEffect(() => {
    if (!isActive || !currentStep?.target) return undefined;

    const handleReposition = () => {
      const element = document.querySelector(
        currentStep.target as string,
      ) as HTMLElement | null;
      updateTargetRect(element);
    };

    window.addEventListener('resize', handleReposition);
    window.addEventListener('scroll', handleReposition, true);

    return () => {
      window.removeEventListener('resize', handleReposition);
      window.removeEventListener('scroll', handleReposition, true);
    };
  }, [currentStep?.target, isActive, updateTargetRect]);

  useEffect(() => {
    if (!isActive) return;

    const activeDealerId = getDealerId(selectedDealer);
    if (dealerId && activeDealerId && dealerId !== activeDealerId) {
      saveAndExit();
    }
  }, [dealerId, isActive, saveAndExit, selectedDealer]);

  useEffect(
    () => () => {
      clearTargetObserver();
    },
    [clearTargetObserver],
  );

  const hasSavedProgress = useMemo(() => {
    if (!config || !dealerId) return false;
    return hasSavedTourProgress(config.id, dealerId, config);
  }, [config, dealerId]);

  const canContinue = hasSavedProgress;

  const value = useMemo<GuidanceTourContextValue>(
    () => ({
      isActive,
      isChecklistOpen,
      config,
      currentStepIndex,
      currentStep,
      dealerId,
      targetRect,
      isPreparingStep,
      isStartingTour,
      stepCompleted,
      stepAccessibility,
      stepValidation,
      isLoadingStepValidation,
      isCurrentStepCompleted,
      canMarkCurrentStepComplete,
      priorIncompleteCount,
      canNavigateBack,
      canNavigateNext,
      progressPercent,
      progressSummary,
      hasSavedProgress,
      canContinue,
      tourStorageRevision,
      startTour,
      continueTour,
      restartTour,
      markComplete,
      nextStep,
      goBack,
      saveAndExit,
      endTourPermanently,
      jumpToStep,
      jumpToPrerequisiteStep,
      openChecklist,
      closeChecklist,
      refreshStepValidation,
      refreshTourProgress,
      updateCrmSupportsEmailReports,
      updateSandboxReadinessRequired,
      crmSupportsEmailReports,
      crmSupportsEmailReportsSelection,
      sandboxReadinessRequired,
      sandboxReadinessRequiredSelection,
      isSavingStepContext,
      getStoredProgress,
    }),
    [
      canContinue,
      canMarkCurrentStepComplete,
      canNavigateBack,
      canNavigateNext,
      closeChecklist,
      config,
      continueTour,
      crmSupportsEmailReports,
      crmSupportsEmailReportsSelection,
      currentStep,
      currentStepIndex,
      dealerId,
      endTourPermanently,
      getStoredProgress,
      goBack,
      hasSavedProgress,
      isActive,
      isChecklistOpen,
      isCurrentStepCompleted,
      isLoadingStepValidation,
      isPreparingStep,
      isStartingTour,
      isSavingStepContext,
      jumpToStep,
      jumpToPrerequisiteStep,
      markCompleteError,
      priorIncompleteCount,
      markComplete,
      nextStep,
      openChecklist,
      progressPercent,
      progressSummary,
      refreshStepValidation,
      refreshTourProgress,
      restartTour,
      sandboxReadinessRequired,
      sandboxReadinessRequiredSelection,
      saveAndExit,
      startTour,
      stepAccessibility,
      stepCompleted,
      stepValidation,
      targetRect,
      tourStorageRevision,
      updateCrmSupportsEmailReports,
      updateSandboxReadinessRequired,
    ],
  );

  return (
    <GuidanceTourContext.Provider value={value}>
      {children}
      {isActive && config && currentStep && (
        <>
          {!launchIntroActive && <GuidanceTourSpotlight targetRect={targetRect} />}
          <GuidanceTourPanel
            tourTitle={config.title ?? 'Launch Readiness'}
            step={currentStep}
            steps={config.steps}
            stepIndex={currentStepIndex}
            stepCompleted={stepCompleted}
            stepAccessibility={stepAccessibility}
            isCurrentStepCompleted={isCurrentStepCompleted}
            canMarkCurrentStepComplete={canMarkCurrentStepComplete}
            priorIncompleteCount={priorIncompleteCount}
            canNavigateBack={canNavigateBack}
            canNavigateNext={canNavigateNext}
            progressPercent={progressPercent}
            showLaunchIntro={launchIntroActive}
            onDismissLaunchIntro={dismissLaunchIntro}
            isPreparingStep={isPreparingStep}
            stepValidation={stepValidation}
            isLoadingStepValidation={isLoadingStepValidation}
            isMarkingComplete={isMarkingComplete}
            markCompleteError={markCompleteError}
            useApiValidation={isApiDrivenTour(config.id)}
            onRefreshValidation={() => {
              void refreshStepValidation();
            }}
            onMarkComplete={() => {
              void markComplete();
            }}
            onNext={nextStep}
            onBack={goBack}
            onSaveAndExit={saveAndExit}
            onRestart={restartTour}
            onJumpToStep={jumpToStep}
            onJumpToPrerequisiteStep={jumpToPrerequisiteStep}
            crmSupportsEmailReports={crmSupportsEmailReports}
            crmSupportsEmailReportsSelection={crmSupportsEmailReportsSelection}
            isSavingStepContext={isSavingStepContext}
            onCrmSupportsEmailReportsChange={value => {
              void updateCrmSupportsEmailReports(value);
            }}
            sandboxReadinessRequired={sandboxReadinessRequired}
            sandboxReadinessRequiredSelection={sandboxReadinessRequiredSelection}
            onSandboxReadinessRequiredChange={value => {
              void updateSandboxReadinessRequired(value);
            }}
          />
        </>
      )}
      <OnboardingLaunchPromptModal
        open={launchPromptOpen}
        onClose={dismissLaunchPrompt}
        onGoToNurturingPlanGenerator={goToNurturingPlanGeneratorFromPrompt}
      />
    </GuidanceTourContext.Provider>
  );
};

export const useGuidanceTour = (): GuidanceTourContextValue => {
  const context = useContext(GuidanceTourContext);
  if (!context) {
    throw new Error('useGuidanceTour must be used within GuidanceTourProvider');
  }
  return context;
};

export const useGuidanceTourOptional = (): GuidanceTourContextValue | null =>
  useContext(GuidanceTourContext);
